import React from 'react';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import RecommendationsSearchResults from '../RecommendationsSearchResults';
import AvailabilitySidebar from '../CommunityAvailabilitySidebar';
import landingIcon from '../RepVisitsTravelPlanPage/stacked-cards.png';

import Toast from '../../components/Toast';
import Item from '../../components/Item';
import Icon from '../../components/Icon';
import Sidebar from '../../components/Sidebar';
import Input from '../../components/Input';
import Image from '../../components/Image';
import Segment from '../../components/Segment';
import PageTitle from '../../components/PageTitle';

import './styles.css';

const messages = defineMessages({
  recommendations: {
    id: 'repvisits.menu.recommendations',
    defaultMessage: 'Recommendations',
  },
  searchBy: {
    id: 'repvisits.recommendations.searchBy',
    defaultMessage: 'Search by location...',
  },
  summary: {
    id: 'repvisits.recommendations.summary',
    defaultMessage: `We analyzed millions of application and student records to determine what makes a student more likely to enroll in
     your institution. Each high school is compared against the profile of past enrollees for your institution and scored on a scale of
     1-100 (with 100 being the best and 50 as the median score). The score includes, but is not limited to, factors such as application
     history, type of high school, student career interest, religious affiliation, willingness to travel, student demographics, and town
     demographics. The list below shows the top high schools and their score based on this analysis.`,
  },
  welcome: {
    id: 'repvisits.recommendations.welcome',
    defaultMessage: 'Welcome to Recommendations',
  },
  purpose: {
    id: 'repvisits.recommendations.purpose',
    defaultMessage:
      'Recommendations help you find schools that match your institution\'s recruitment goals. Select a state or county to begin!',
  },
  successDescription: {
    id: 'repvisits.travel.plan.successDescription',
    defaultMessage: 'School added to Travel Plan',
  },
  failureDescription: {
    id: 'repvisits.travel.plan.failureDescription',
    defaultMessage: 'Something unexpected happened. Please try your request again.',
  },
});

function buildRecommendationsParams(props) {
  const searchText = get(props, 'location.query.keywords');
  return { filter: searchText };
}

class RepVisitsRecommendationsPage extends React.Component {
  static propTypes = {
    intl: PropTypes.object,
    router: PropTypes.object,
    location: PropTypes.object,
  };

  successNotification =
    <Toast
      active
      title=""
      kind="success"
      description={this.props.intl.formatMessage(messages.successDescription)}
      onDeactivate={() => this.setState({ notification: null })}
    />;

  failureNotification =
    <Toast
      active
      title=""
      kind="error"
      description={this.props.intl.formatMessage(messages.failureDescription)}
      onDeactivate={() => this.setState({ notification: null })}
    />;

  constructor(props, context) {
    super(props, context);
    const searchText = get(props, 'location.query.keywords');
    this.state = {
      searchText,
      notification: null,
      queryParams: buildRecommendationsParams(props),
      sidebarVisible: false,
      sidebarSchoolId: null,
    };
    this.updateRouteDebounce = debounce(this.updateRoute, 300);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.location !== nextProps.location) {
      this.setState({ queryParams: buildRecommendationsParams(nextProps) });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !isEqual(this.state, nextState) || this.props.location !== nextProps.location;
  }

  handleTravelPlanCallback = (status) => {
    if (!status) {
      this.setState({ notification: this.failureNotification });
    } else {
      this.setState({ notification: this.successNotification });
    }
  };

  closeSidebar = () => {
    if (this.state.sidebarVisible) {
      this.setState({ sidebarVisible: false });
    }
  };

  openSidebar = (schoolId) => {
    this.setState({ sidebarVisible: true, sidebarSchoolId: schoolId });
  };

  updateRoute = () => {
    const { searchText } = this.state;
    const { router } = this.props;

    router.push({
      pathname: '/rep-visits/recommendations',
      query: {
        keywords: searchText,
      },
    });
  };

  handleSearch = (event) => {
    const searchText = event.target.value;
    const nextState = { searchText, notification: null };
    if (!searchText) {
      this.updateRouteDebounce.cancel();
      this.setState(nextState, this.updateRoute);
    } else {
      this.setState(nextState, this.updateRouteDebounce);
    }
  };

  render() {
    const { intl } = this.props;
    const { notification, searchText, sidebarVisible, sidebarSchoolId, queryParams } = this.state;

    return (
      <Sidebar.Pushable styleName="sidebar">
        <PageTitle subTitle="Recommendations" title="RepVisits" category="Intersect" />
        <Sidebar.Pusher styleName="container-border" dimmed={sidebarVisible} onClick={this.closeSidebar}>
          <h1 id="pageTitle">
            <FormattedMessage {...messages.recommendations} />
          </h1>
          <div styleName="description">
            <FormattedMessage {...messages.summary} />
          </div>
          {notification}
          <Segment.Group styleName="content-grid" data-cy="repvisit-recommendations">
            <Segment styleName="search-row">
              <Input
                data-cy="repvisit-recommendations-search"
                onChange={this.handleSearch}
                size="small"
                value={searchText}
                icon={<Icon name="search" link styleName="search-button-icon" />}
                placeholder={intl.formatMessage(messages.searchBy)}
                input={{
                  'aria-label': intl.formatMessage(messages.searchBy),
                  'content': <div styleName="input" />,
                }}
                styleName="form"
              />
            </Segment>
            <Segment>
              {queryParams.filter ? (
                <RecommendationsSearchResults
                  availabilityCallback={this.openSidebar}
                  params={queryParams}
                  travelPlanCallback={this.handleTravelPlanCallback}
                />
              ): (
                <Item styleName="landing-item">
                  <Image src={landingIcon} styleName="landing-icon" />
                  <div styleName="center-text"> {intl.formatMessage(messages.welcome)} </div>
                  <div styleName="center-text"> {intl.formatMessage(messages.purpose)} </div>
                </Item>
              )}
            </Segment>
          </Segment.Group>
        </Sidebar.Pusher>
        <AvailabilitySidebar
          visible={sidebarVisible}
          closeFunction={this.closeSidebar}
          schoolId={sidebarSchoolId}
        />
      </Sidebar.Pushable>
    );
  }
}

export default injectIntl(RepVisitsRecommendationsPage);
